import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCurrency as fmtCurrency, useCurrency } from '@/lib/currency';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Pencil, Trash2, Pause, Play, Asterisk } from 'lucide-react';
import { cn } from '@/lib/utils';

const RECURRENCE_LABELS = {
  one_time: 'One Time',
  weekly: 'Weekly',
  bi_weekly: 'Bi-Weekly',
  semi_monthly: 'Semi-Monthly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
  custom: 'Custom',
  none: 'Sporadic',
};

// Helper functions for sporadic transactions
const getSporadicTotal = (tx) => {
  if (!tx.custom_amounts) return 0;
  return Object.values(tx.custom_amounts).reduce((sum, amt) => sum + amt, 0);
};

const formatSporadicAmount = (tx) => {
  if (!tx.custom_amounts || Object.keys(tx.custom_amounts).length === 0) {
    return '--';
  }
  const amounts = Object.values(tx.custom_amounts);
  const min = Math.min(...amounts.map(Math.abs));
  const max = Math.max(...amounts.map(Math.abs));
  if (min === max) {
    return `$${min.toFixed(2)} each`;
  }
  return `$${min.toFixed(2)} - $${max.toFixed(2)}`;
};

export const TransactionList = ({
  open,
  onOpenChange,
  transactions,
  onEdit,
  onDelete,
  onPause,
  onResume,
}) => {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currencyCode] = useCurrency();

  const formatCurrency = (amount) => fmtCurrency(Math.abs(amount), currencyCode);

  const handleDelete = () => {
    if (deleteTarget) {
      onDelete(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  // Helper to determine if a transaction is income or expense
  // For sporadic transactions (amount is null), check the total of custom_amounts
  const isIncome = (tx) => {
    if (tx.recurrence_type === 'none') {
      return getSporadicTotal(tx) > 0;
    }
    return tx.amount > 0;
  };

  const incomeTransactions = transactions.filter(t => isIncome(t));
  const expenseTransactions = transactions.filter(t => !isIncome(t));

  const TransactionCard = ({ tx }) => (
    <div
      data-testid={`transaction-card-${tx.id}`}
      className={cn(
        'p-4 rounded-lg border bg-card hover:bg-accent/30',
        'transition-colors duration-200',
        tx.is_hypothetical && 'border-dashed border-muted-foreground/40',
        tx.paused && 'opacity-50'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium truncate">{tx.name}</span>
            {tx.is_hypothetical && (
              <Asterisk className="w-3 h-3 text-muted-foreground flex-shrink-0" />
            )}
            {tx.paused && (
              <Badge variant="secondary" className="text-xs">Paused</Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="text-xs font-normal">
              {RECURRENCE_LABELS[tx.recurrence_type] || tx.recurrence_type}
              {tx.recurrence_type === 'none' && tx.custom_amounts && (
                <span className="ml-1">({Object.keys(tx.custom_amounts).length} occurrences)</span>
              )}
            </Badge>
            <span>from {tx.start_date}</span>
            {tx.end_date && <span>to {tx.end_date}</span>}
          </div>
        </div>
        <span className={cn(
          'font-mono font-bold whitespace-nowrap',
          (tx.recurrence_type === 'none' ? getSporadicTotal(tx) : tx.amount) > 0 
            ? 'text-green-600 dark:text-green-400' 
            : 'text-foreground'
        )}>
          {tx.recurrence_type === 'none' 
            ? formatSporadicAmount(tx)
            : `${tx.amount > 0 ? '+' : '-'}${formatCurrency(tx.amount)}`
          }
        </span>
      </div>
      
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(tx)}
          data-testid={`edit-tx-${tx.id}`}
        >
          <Pencil className="w-4 h-4 mr-1" /> Edit
        </Button>
        {tx.recurrence_type !== 'one_time' && tx.recurrence_type !== 'none' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => tx.paused ? onResume(tx.id) : onPause(tx.id)}
            data-testid={`pause-tx-${tx.id}`}
          >
            {tx.paused ? (
              <><Play className="w-4 h-4 mr-1" /> Resume</>
            ) : (
              <><Pause className="w-4 h-4 mr-1" /> Pause</>
            )}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDeleteTarget(tx)}
          className="text-destructive hover:text-destructive"
          data-testid={`delete-tx-${tx.id}`}
        >
          <Trash2 className="w-4 h-4 mr-1" /> Delete
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-[500px] overflow-y-auto" data-testid="transaction-list-panel">
          <SheetHeader>
            <SheetTitle className="font-heading text-xl">Manage Transactions</SheetTitle>
            <SheetDescription>
              View, edit, pause, or delete your recurring transactions.
            </SheetDescription>
          </SheetHeader>

          <div className="py-6 space-y-6">
            {/* Income Section */}
            <div>
              <h3 className="font-heading font-semibold text-green-600 dark:text-green-400 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Income ({incomeTransactions.length})
              </h3>
              {incomeTransactions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No income transactions yet.</p>
              ) : (
                <div className="space-y-3">
                  {incomeTransactions.map(tx => (
                    <TransactionCard key={tx.id} tx={tx} />
                  ))}
                </div>
              )}
            </div>

            {/* Expenses Section */}
            <div>
              <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-foreground"></span>
                Expenses ({expenseTransactions.length})
              </h3>
              {expenseTransactions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No expense transactions yet.</p>
              ) : (
                <div className="space-y-3">
                  {expenseTransactions.map(tx => (
                    <TransactionCard key={tx.id} tx={tx} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget && deleteTarget.recurrence_type !== 'one_time' && deleteTarget.recurrence_type !== 'none'
                ? 'Delete recurring transaction?'
                : 'Delete this transaction?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && deleteTarget.recurrence_type !== 'one_time' && deleteTarget.recurrence_type !== 'none' ? (
                <>
                  This will permanently delete{' '}
                  <strong>{deleteTarget?.name}</strong> and{' '}
                  <strong>all of its future occurrences</strong> from your forecast.
                  <br /><br />
                  If you only want to skip a single occurrence, cancel here — then customize
                  that specific date&rsquo;s amount to <strong>0</strong> in the &ldquo;Customize
                  Future Amounts&rdquo; section instead of deleting the whole transaction.
                </>
              ) : (
                <>
                  This will permanently delete{' '}
                  <strong>{deleteTarget?.name}</strong> from your forecast. This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleteTarget && deleteTarget.recurrence_type !== 'one_time' && deleteTarget.recurrence_type !== 'none'
                ? 'Delete all occurrences'
                : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
